import { FC, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from 'antd';
import LoginOutlined from '@ant-design/icons/LoginOutlined';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppConfig } from '@/config';
import DevLoginForm from '@/components/DevLoginForm';
import AuthCard from '@/features/signin/AuthCard';
import styles from '@/features/signin/signin.module.css';

/**
 * Sign-in (spec §2): logo, heading, Auth0, language switch, release label and
 * company/terms/privacy links. After an expired session it says so. The
 * development form appears only in VITE_DEV_LOGIN builds, never in production.
 */
const SignInPage: FC = () => {
  const { t } = useTranslation();
  const { loginWithRedirect, isLoading } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [authError, setAuthError] = useState(false);
  const expired = searchParams.get('reason') === 'expired';

  const handleSignIn = async () => {
    setAuthError(false);
    try {
      const returnTo = (location.state as any)?.from?.pathname || '/';
      await loginWithRedirect({ appState: { returnTo } });
    } catch {
      setAuthError(true);
    }
  };

  return (
    <AuthCard title={t('v2.signIn.signIn')} lead={t('v2.signIn.welcome')}>
      {expired ? (
        <p role="alert" className={styles.notice}>
          {t('v2.signIn.sessionExpired')}
        </p>
      ) : null}
      <div className={styles.actions}>
        <Button
          type="primary"
          size="large"
          icon={<LoginOutlined aria-hidden />}
          onClick={handleSignIn}
          loading={isLoading}
          block
          className={styles.primary}
          aria-describedby={authError ? 'signin-auth-error' : undefined}
        >
          {t('v2.signIn.signInAuth0')}
        </Button>
        {authError ? (
          <p id="signin-auth-error" role="alert" className={styles.error}>
            {t('v2.signIn.authError')}
          </p>
        ) : null}
        {AppConfig.devLogin.enabled && <DevLoginForm />}
      </div>
      <p className={styles.alternate}>
        {t('v2.signIn.noAccount')}{' '}
        <button
          type="button"
          className={styles.link}
          onClick={() => navigate('/signup')}
        >
          {t('v2.signIn.signUp')}
        </button>
      </p>
    </AuthCard>
  );
};

export default SignInPage;
